// Single source of truth for refreshing the access token.
//
// Both the proactive path (authService's scheduled/visibility/extend-session
// timers) and the reactive path (RTK Query's refresh-on-401 base query) call
// this. It performs the HTTP refresh with `fetch` (no axios), updates
// localStorage + Redux, and coalesces concurrent callers so a burst of 401s
// plus a scheduled refresh trigger a single network round-trip.
import { store } from '@store/index';
import { refreshTokenSuccess } from '@store/slices/authSlice';
import { AUTH_STORAGE_KEYS } from '../config/auth';
import { API_CONFIG } from '../config/api';

export interface RefreshResult {
  token: string;
  expiresAt: string;
}

let inflight: Promise<RefreshResult> | null = null;

export async function refreshAccessToken(): Promise<RefreshResult> {
  if (inflight) {
    return inflight;
  }
  inflight = doRefresh().finally(() => {
    inflight = null;
  });
  return inflight;
}

async function doRefresh(): Promise<RefreshResult> {
  const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  // Admin tokens refresh via the bearer token; device tokens send the refresh
  // token in the body (mirrors the previous authService behaviour).
  const isAdminToken = store.getState().auth.user?.role === 'admin';
  const accessToken = localStorage.getItem(AUTH_STORAGE_KEYS.token);

  const response = await fetch(
    `${API_CONFIG.BASE_URL}${isAdminToken ? '/auth/admin/refresh' : '/auth/refresh'}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(isAdminToken ? {} : { refreshToken }),
    }
  );

  if (!response.ok) {
    throw new Error(`Token refresh failed (${response.status})`);
  }

  const data = await response.json();
  // Admin refresh returns access_token, device refresh returns token.
  const token: string = data.access_token || data.token;
  const expiresAt: string =
    data.expiresAt || new Date(Date.now() + data.expires_in * 1000).toISOString();

  localStorage.setItem(AUTH_STORAGE_KEYS.token, token);
  localStorage.setItem(AUTH_STORAGE_KEYS.tokenExpiresAt, expiresAt);
  store.dispatch(refreshTokenSuccess({ token, expiresAt }));

  return { token, expiresAt };
}
